import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MantaRayIcon } from '@/components/icons/MantaRayIcon';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import { Feature } from '@/contexts/FeaturesContext';
import {
  BookOpen,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Settings,
  Shield,
  Wallet,
  X,
  PlusCircle,
  House,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { COURSE_ABOUT_HREF, COURSE_HOW_HREF, MARKETING_CTAS } from '@/lib/marketingCtas';

const WHAT_WE_DO_ITEMS = [
  { label: 'For Students', path: '/what-we-do/students/', description: 'Online tutoring, homework help, and test prep' },
  { label: 'For Parents', path: '/what-we-do/parents/', description: 'Help your child improve grades and build confidence' },
  { label: 'For Tutors', path: '/what-we-do/tutors/', description: 'Grow your tutoring business with built-in tools' },
  { label: 'Inside the SAT course', path: COURSE_ABOUT_HREF, description: "What's in the free course" },
];

const HOW_IT_WORKS_ITEMS = [
  { label: 'The SAT course', path: COURSE_HOW_HREF },
  { label: 'For Students', path: '/how-it-works/students/' },
  { label: 'For Parents', path: '/how-it-works/parents/' },
  { label: 'For Tutors', path: '/how-it-works/tutors/' },
];

const APP_MORE_ITEMS = [
  { label: 'Sessions', path: '/sessions', icon: ClipboardList, hiddenForParent: true },
  { label: 'Calendar', path: '/calendar', icon: CalendarDays },
  { label: 'Payments', path: '/payments', icon: Wallet },
  { label: 'Messages', path: '/chats', icon: MessageSquare },
  { label: 'Settings', path: '/settings', icon: Settings },
];

function isPathActive(pathname: string, path: string) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function activeTextClass(isActive: boolean, className?: string) {
  return cn(
    className,
    isActive ? 'font-semibold text-primary dark:text-primary-foreground' : 'text-foreground'
  );
}

function getInitials(fullName?: string | null) {
  if (!fullName) return '?';
  return fullName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function AppNavLink({
  to,
  icon: Icon,
  children,
  activeClassName,
}: {
  to: string;
  icon: LucideIcon;
  children: ReactNode;
  activeClassName?: string;
}) {
  const location = useLocation();
  const isActive = isPathActive(location.pathname, to);

  return (
    <Link
      to={to}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'relative inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-sm font-medium transition-colors',
        'hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isActive
          ? 'font-semibold text-primary dark:text-primary-foreground'
          : 'text-muted-foreground',
        isActive && activeClassName
      )}
    >
      <Icon className="h-4 w-4" />
      <span className="hidden sm:inline">{children}</span>
    </Link>
  );
}

function NavDropdown({
  label,
  items,
  withDescriptions = false,
}: {
  label: string;
  items: { label: string; path: string; description?: string }[];
  withDescriptions?: boolean;
}) {
  const location = useLocation();
  const isActive = items.some((item) => isPathActive(location.pathname, item.path.replace(/\/$/, '')));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${label} navigation menu`}
          className={cn(
            'flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            'hover:bg-muted hover:text-foreground focus:outline-none',
            isActive ? 'font-semibold text-primary dark:text-primary-foreground' : 'text-muted-foreground'
          )}
        >
          {label}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={withDescriptions ? 'w-64' : 'w-48'}>
        {items.map((item) => (
          <DropdownMenuItem key={item.path} asChild className={activeTextClass(isPathActive(location.pathname, item.path.replace(/\/$/, '')))}>
            <Link
              to={item.path}
              aria-current={isPathActive(location.pathname, item.path.replace(/\/$/, '')) ? 'page' : undefined}
              className={cn('cursor-pointer', activeTextClass(isPathActive(location.pathname, item.path.replace(/\/$/, ''))))}
            >
              <div>
                <div className="font-medium">{item.label}</div>
                {withDescriptions && item.description && (
                  <div className="mt-0.5 text-xs text-muted-foreground">{item.description}</div>
                )}
              </div>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Top-level course link: a 6px primary dot marks it as the front door. Active on any /what-we-do/sat* route. */
function CourseNavLink() {
  const location = useLocation();
  const isActive = location.pathname.startsWith('/what-we-do/sat');

  return (
    <Link
      to={MARKETING_CTAS.courseNav.href}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        'hover:bg-muted hover:text-foreground',
        isActive ? 'font-semibold text-primary dark:text-primary-foreground' : 'text-foreground'
      )}
    >
      {MARKETING_CTAS.courseNav.label}
      <span aria-hidden="true" data-testid="course-dot" className="h-1.5 w-1.5 rounded-full bg-primary dark:bg-rose-400" />
    </Link>
  );
}

function NavLink({ to, children }: { to: string; children: ReactNode }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={cn(
        'rounded-md px-3 py-2 text-sm font-medium transition-colors',
        'hover:bg-muted hover:text-foreground',
        isActive ? 'text-foreground' : 'text-muted-foreground'
      )}
    >
      {children}
    </Link>
  );
}

function MoreMenu() {
  const { user } = useAuth();
  const location = useLocation();
  const visibleMoreItems = APP_MORE_ITEMS.filter((item) => (
    user?.role === 'PARENT' ? item.path === '/calendar' : !item.hiddenForParent
  ));
  const morePaths = visibleMoreItems.map((item) => item.path);
  const isActive = morePaths.some((path) => isPathActive(location.pathname, path))
    || (user?.role === 'ADMIN' && isPathActive(location.pathname, '/admin'));
  const dropdownLinkClass = (path: string, className?: string, preserveActiveColor = false) => cn(
    'flex w-full items-center gap-2',
    preserveActiveColor
      ? cn(className, isPathActive(location.pathname, path) && 'font-semibold')
      : activeTextClass(isPathActive(location.pathname, path), className)
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-current={isActive ? 'page' : undefined}
          className={cn(
            'relative gap-1.5 px-2',
            isActive ? 'font-semibold text-primary dark:text-primary-foreground' : 'text-muted-foreground'
          )}
        >
          More
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {visibleMoreItems.map((item) => (
          <DropdownMenuItem key={item.path} asChild className={activeTextClass(isPathActive(location.pathname, item.path))}>
            <Link to={item.path} aria-current={isPathActive(location.pathname, item.path) ? 'page' : undefined} className={dropdownLinkClass(item.path)}>
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}

        {Feature.ORGANIZATIONS.isActive() && (
          <>
            <div className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Organization
            </div>
            <div className="px-2 pb-2">
              <OrgSwitcher />
            </div>
          </>
        )}

        {user?.role === 'ADMIN' && (
          <DropdownMenuItem
            asChild
            className={cn(
              'text-purple-600 dark:text-purple-400',
              isPathActive(location.pathname, '/admin') && 'font-semibold'
            )}
          >
            <Link to="/admin" aria-current={isPathActive(location.pathname, '/admin') ? 'page' : undefined} className={dropdownLinkClass('/admin', 'text-purple-600 dark:text-purple-400', true)}>
              <Shield className="h-4 w-4" />
              Admin
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountMenu() {
  const { user, sessions, activeSessionIndex, switchSession, logout, isImpersonating, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = useMemo(() => getInitials(user?.fullName), [user?.fullName]);

  const handleLogout = async () => {
    setOpen(false);
    if (isImpersonating) {
      await stopImpersonation();
      return;
    }
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed', error);
    }
    navigate('/login');
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 px-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-rose-100 text-xs font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
            {initials}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="border-b border-border px-4 py-3 bg-muted/30">
          <p className="text-sm font-medium">{user?.fullName}</p>
          <p className="text-xs text-muted-foreground">{user?.email}</p>
          <span className="mt-1 inline-block rounded-full bg-secondary px-2 py-0.5 text-xs">{user?.role}</span>
        </div>

        {sessions.length > 1 && (
          <div className="border-b border-border py-1">
            <p className="px-4 py-1 text-xs font-medium text-muted-foreground">Switch account</p>
            {sessions.map((session, idx) => (
              <button
                key={session.user.email}
                className={`flex w-full items-center px-4 py-2 text-left text-sm hover:bg-accent ${idx === activeSessionIndex ? 'bg-accent font-medium' : ''}`}
                onClick={() => {
                  switchSession(idx);
                  setOpen(false);
                }}
              >
                <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                  {getInitials(session.user.fullName)}
                </span>
                <span>{session.user.fullName}</span>
                <span className="ml-auto text-xs text-muted-foreground">{session.user.role}</span>
              </button>
            ))}
          </div>
        )}

        <div className="py-1">
          <DropdownMenuItem asChild>
            <Link to="/login" className="flex w-full items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Add another login
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings" className="flex w-full items-center gap-2">
              <Mail className="h-4 w-4" />
              Account settings
            </Link>
          </DropdownMenuItem>
        </div>

        <div className="border-t border-border py-1">
          <button
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-destructive hover:bg-accent"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LoggedInMobileMenu({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const location = useLocation();
  const mobileLinkClass = (path: string) => cn(
    'block rounded-md px-3 py-2 text-sm font-medium hover:bg-muted',
    isPathActive(location.pathname, path) && 'font-semibold text-primary dark:text-primary-foreground'
  );

  return (
    <div className="space-y-3 border-t border-border/40 p-4">
      {user?.role !== 'PARENT' && (
        <Button className="w-full" asChild>
          <Link to="/request-session" onClick={onClose}>Request Session</Link>
        </Button>
      )}
      <Link to="/dashboard" onClick={onClose} aria-current={isPathActive(location.pathname, '/dashboard') ? 'page' : undefined} className={mobileLinkClass('/dashboard')}>
        Home
      </Link>
      {user?.satEnabled && (
        <Link to="/sat" onClick={onClose} aria-current={isPathActive(location.pathname, '/sat') ? 'page' : undefined} className={mobileLinkClass('/sat')}>
          SAT Prep
        </Link>
      )}
      <div className="space-y-1 pt-2">
        {user?.role !== 'PARENT' && (
          <Link to="/sessions" onClick={onClose} aria-current={isPathActive(location.pathname, '/sessions') ? 'page' : undefined} className={mobileLinkClass('/sessions')}>
            Sessions
          </Link>
        )}
        <Link to="/calendar" onClick={onClose} aria-current={isPathActive(location.pathname, '/calendar') ? 'page' : undefined} className={mobileLinkClass('/calendar')}>
          Calendar
        </Link>
        <Link to="/payments" onClick={onClose} aria-current={isPathActive(location.pathname, '/payments') ? 'page' : undefined} className={mobileLinkClass('/payments')}>
          Payments
        </Link>
        <Link to="/chats" onClick={onClose} aria-current={isPathActive(location.pathname, '/chats') ? 'page' : undefined} className={mobileLinkClass('/chats')}>
          Messages
        </Link>
        <Link to="/settings" onClick={onClose} aria-current={isPathActive(location.pathname, '/settings') ? 'page' : undefined} className={mobileLinkClass('/settings')}>
          Settings
        </Link>
      </div>
    </div>
  );
}

function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { isAuthenticated } = useAuth();
  const [whatWeDoOpen, setWhatWeDoOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-sm lg:hidden">
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-border/40 p-3">
          <Link to={isAuthenticated ? '/dashboard' : '/'} onClick={onClose} className="flex items-center gap-3.5">
            <MantaRayIcon className="animate-swim" />
            <span className="hidden text-sm font-bold text-rose-900 dark:text-rose-300 sm:inline">Mentara</span>
          </Link>
          <button type="button" aria-label="Close navigation menu" onClick={onClose} className="rounded-md p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          <Link
            to={MARKETING_CTAS.courseNav.href}
            onClick={onClose}
            className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-primary hover:bg-muted dark:text-primary-foreground"
          >
            {MARKETING_CTAS.courseNav.label}
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-primary dark:bg-rose-400" />
          </Link>
          <button
            type="button"
            aria-label="Toggle What We Do section"
            onClick={() => setWhatWeDoOpen((value) => !value)}
            className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted"
          >
            What We Do
            <ChevronDown className={cn('h-4 w-4 transition-transform', whatWeDoOpen && 'rotate-180')} />
          </button>
          {whatWeDoOpen && (
            <div className="space-y-1 pl-4">
              {WHAT_WE_DO_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className="block rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}

          <button
            type="button"
            aria-label="Toggle How It Works section"
            onClick={() => setHowItWorksOpen((value) => !value)}
            className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted"
          >
            How It Works
            <ChevronDown className={cn('h-4 w-4 transition-transform', howItWorksOpen && 'rotate-180')} />
          </button>
          {howItWorksOpen && (
            <div className="space-y-1 pl-4">
              {HOW_IT_WORKS_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className="block rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}

          <Link to="/about" onClick={onClose} className="block rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted">
            About
          </Link>
          <Link to="/blog" onClick={onClose} className="block rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted">
            Blog
          </Link>
          <Link to="/faq" onClick={onClose} className="block rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted">
            FAQ
          </Link>

          {isAuthenticated ? (
            <LoggedInMobileMenu onClose={onClose} />
          ) : (
            <div className="space-y-2 border-t border-border/40 p-4">
              <Button variant="outline" className="w-full" asChild>
                <Link to={MARKETING_CTAS.login.href} onClick={onClose}>{MARKETING_CTAS.login.label}</Link>
              </Button>
              <Button className="w-full" asChild>
                <Link to={MARKETING_CTAS.createAccount.href} onClick={onClose}>{MARKETING_CTAS.createAccount.label}</Link>
              </Button>
            </div>
          )}
        </nav>
      </div>
    </div>
  );
}

export function MarketingTopbar() {
  const { isAuthenticated, user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Spec 010 HERO-V2: on `/` the hero owns the one filled primary, so this CTA steps back to outline.
  const onHome = useLocation().pathname === '/';

  return (
    <>
      <header className="sticky top-0 z-40 h-14 w-full border-b border-border/40 bg-background/95 backdrop-blur-sm">
        <div className="container flex h-full items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <Link
              to={isAuthenticated ? '/dashboard' : '/'}
              className="group mr-1 flex shrink-0 items-center gap-2 transition-transform duration-200 hover:scale-105 sm:mr-4 sm:gap-3"
            >
              <MantaRayIcon className="animate-swim" />
              <span className="hidden font-bold text-rose-900 dark:text-rose-300 sm:inline">Mentara</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-0.5">
              {!isAuthenticated && <CourseNavLink />}
              <NavDropdown label="What We Do" items={WHAT_WE_DO_ITEMS} withDescriptions />
              <NavDropdown label="How It Works" items={HOW_IT_WORKS_ITEMS} />
              <NavLink to="/about">About</NavLink>
              <NavLink to="/blog">Blog</NavLink>
              <NavLink to="/faq">FAQ</NavLink>
            </nav>
          </div>

          <div className="ml-auto flex shrink-0 flex-nowrap items-center gap-1 sm:gap-2">
            {isAuthenticated ? (
              <>
                {user?.role !== 'PARENT' && (
                  <Button size="sm" asChild>
                    <Link to="/request-session" className="flex items-center gap-1.5">
                      <PlusCircle className="h-4 w-4" />
                      <span className="hidden sm:inline">Request</span>
                    </Link>
                  </Button>
                )}
                <AppNavLink to="/dashboard" icon={House}>Home</AppNavLink>
                {user?.satEnabled && (
                  <AppNavLink to="/sat" icon={BookOpen} activeClassName="text-blue-700 dark:text-blue-300">
                    SAT Prep
                  </AppNavLink>
                )}
                <MoreMenu />
                <AccountMenu />
              </>
            ) : (
              <>
                <div className="flex flex-nowrap items-center gap-0.5 lg:hidden">
                  <Link
                    to="/login"
                    className="shrink-0 whitespace-nowrap rounded-md px-1.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground sm:px-2"
                  >
                    Log in
                  </Link>
                  <Link
                    to={MARKETING_CTAS.topbarPrimaryCompact.href}
                    className={cn(
                      'shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold',
                      onHome
                        ? 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90',
                    )}
                  >
                    {MARKETING_CTAS.topbarPrimaryCompact.label}
                  </Link>
                </div>
                <div className="hidden items-center gap-2 lg:flex">
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/login">Log in</Link>
                  </Button>
                  <Button size="sm" variant={onHome ? 'outline' : 'default'} asChild>
                    <Link to={MARKETING_CTAS.topbarPrimary.href}>{MARKETING_CTAS.topbarPrimary.label}</Link>
                  </Button>
                </div>
              </>
            )}
            <button
              type="button"
              aria-label="Open navigation menu"
              onClick={() => setMobileOpen(true)}
              className="shrink-0 rounded-md p-2 hover:bg-muted lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
